import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Paper,
    Typography,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Box,
    LinearProgress,
    FormControl,
    Autocomplete,
    TextField,
    IconButton,
    Button,
    styled,
    Card,
    CardContent,
    Divider, Alert,
Snackbar} from '@mui/material';
import {
    Assignment as AssignmentIcon,
    SupervisorAccount as SupervisorAccountIcon,
    AccountCircle as AccountCircleIcon,
    Groups as GroupsIcon,
    Business as BusinessIcon,
    Flag as FlagIcon,
    Speed as SpeedIcon,
    Add as AddIcon,
    Cancel as CancelIcon,
    PersonAdd as PersonAddIcon,
    GroupAdd as GroupAddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { axiosInstance, localAxiosGet, localAxiosToken } from '../../../Axios/axios';
import { set } from 'date-fns';

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: '12px',
        padding: theme.spacing(2),
        minWidth: '800px',
        maxHeight: '90vh'
    }
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    height: '100%',
    background: 'linear-gradient(135deg, #E3EEFF 0%, #F3E7FF 100%)',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
    }
}));

const TeamCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    '&:hover': {
        borderColor: '#3b82f6',
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
    }
}));

const UpdateAssignTeam = ({
    open,
    onClose,
    selectedProject,
    selectedProjectId,
    accessToken,
    departmentId: currentDepartmentId,
    organizationId,
    onTeamAssignSuccess
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [projectDetails, setProjectDetails] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [addedTeams, setAddedTeams] = useState([]);
    const [existingTeamsData, setExistingTeamsData] = useState([]);
    const [allTeamMembersForExisting, setAllTeamMembersForExisting] = useState({});
    const [editedExistingTeams, setEditedExistingTeams] = useState({});
     const [initialTeamsState, setInitialTeamsState] = useState({ // Add initial state
        addedTeams: [],
        existingTeams: {}
    });
    const [snackbar,setSnackbar]=useState({open:false,message:'',severity:'success'});

    // Fetch project details
    useEffect(() => {
        if (open && selectedProjectId) {
            fetchProjectDetails();
            fetchDepartments();
            // fetchExistingTeamsData();
        }
    }, [open, selectedProjectId]);

    // useEffect(() => {
    //     console.log('Project Details Updated:', projectDetails);
    // }, [projectDetails]);

    // useEffect(() => {
    //     console.log('Existing Teams Data Updated:', existingTeamsData);
    // }, [existingTeamsData]);

    // useEffect(() => {
    //     console.log('Edited Existing Teams Updated:', editedExistingTeams);
    // }, [editedExistingTeams]);

     // Check if there are any changes
    const hasChanges = () => {
        // Check if any teams were added
        if (addedTeams.length > 0) return true;

        // Check if any existing teams were modified
        const currentExistingTeams = Object.keys(editedExistingTeams);
        const initialExistingTeams = Object.keys(initialTeamsState.existingTeams);

        // If number of teams changed
        if (currentExistingTeams.length !== initialExistingTeams.length) return true;

        // If any team members were modified
        for (const teamId of currentExistingTeams) {
            const currentMembers = editedExistingTeams[teamId] || [];
            const initialMembers = initialTeamsState.existingTeams[teamId] || [];
            
            // Check if members count changed
            if (currentMembers.length !== initialMembers.length) return true;
            
            // Check if specific members changed
            const currentMemberIds = currentMembers.map(m => m.id).sort();
            const initialMemberIds = initialMembers.map(m => m.id).sort();
            
            if (JSON.stringify(currentMemberIds) !== JSON.stringify(initialMemberIds)) {
                return true;
            }
        }

        return false;
    };


    const fetchProjectDetails = async () => {
        try {
            const response = await axiosInstance(localAxiosGet(`/project/getProjectById/${selectedProjectId}`, accessToken));
            const projectData = response.data.message.data;
            setProjectDetails(projectData);

            // Process existing teams from project data
            if (projectData?.departments) {
                const allExistingTeams = [];
                const initialEditedTeams = {};

                projectData.departments.forEach(department => {
                    if (department.teams) {
                        department.teams.forEach(team => {
                            // Convert team members to the format expected by Autocomplete
                            const formattedMembers = team.members ? team.members.map(member => ({
                                id: member.id,
                                firstName: member.first_name,
                                lastName: member.last_name,
                            })) : [];

                            allExistingTeams.push({
                                team_id: team.team_id,
                                team_name: team.team_name,
                                department_id: department.department_id,
                                department_name: department.department_name,
                                members: team.members || [],
                                allAvailableMembers: null // Will be loaded when needed
                            });

                            // Initialize edited teams state with current members
                            initialEditedTeams[team.team_id] = formattedMembers;
                        });
                    }
                });

                setExistingTeamsData(allExistingTeams);
                setEditedExistingTeams(initialEditedTeams);
                setInitialTeamsState({
                    addedTeams: [],
                    existingTeams: { ...initialEditedTeams }
                });
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
        }
    };

    const fetchDepartments = async () => {
        try {
            const response = await axiosInstance(localAxiosGet(`/organization/getAllDepartmentsByOrganizationId/${organizationId}`, accessToken));
            if (response.data?.message?.departments) {
                setDepartments(response.data.message.departments);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    const fetchTeamsData = async (departmentId) => {
        try {
            console.log('Fetching teams for department:', departmentId); // Add this for debugging
            const response = await axiosInstance(localAxiosGet(`/organization/getAllTeamsByDepartmentId/${departmentId}`, accessToken));
            console.log('Teams API response:', response.data); // Add this for debugging

            if (response.data?.message?.teams) {
                const mappedTeams = response.data.message.teams.map((team) => ({
                    id: team.id,
                    name: team.team
                }));
                console.log('Mapped teams:', mappedTeams); // Add this for debugging
                return mappedTeams;
            }
            return [];
        } catch (error) {
            console.error('Error in fetching teams data:', error);
            return [];
        }
    };


    const fetchAllTeamMembers = async (teamId) => {
        try {
            const response = await axiosInstance(localAxiosGet(`/organization/getAllUsersByTeamId/${teamId}`, accessToken));
            return response.data?.message?.users?.map((user) => ({
                id: user.id,
                firstName: user.first_name,
                lastName: user.last_name
            })) || [];
        } catch (error) {
            console.error(`Error fetching all members for team ${teamId}:`, error);
            return [];
        }
    };

    const handleAddTeamMember = () => {
        setAddedTeams(prevAddedTeams => [...prevAddedTeams, {
            id: Date.now(),
            type: 'new_department',
            selectedDepartment: null,
            availableTeams: [],
            selectedTeam: null,
            teamMembersOptions: [],
            selectedTeamMembers: []
        }]);
    };



    const fetchTeamsForAddedTeam = async (index, departmentId) => {
        console.log('fetchTeamsForAddedTeam called with index:', index, 'departmentId:', departmentId); // Debug
        const teams = await fetchTeamsData(departmentId);
        console.log('Teams received:', teams); // Debug

        setAddedTeams(prevAddedTeams => {
            const updatedTeams = [...prevAddedTeams];
            console.log('Current addedTeams:', prevAddedTeams); // Debug
            console.log('Updating index:', index); // Debug

            if (updatedTeams[index]) {
                updatedTeams[index] = {
                    ...updatedTeams[index],
                    availableTeams: teams
                };
                console.log('Updated team at index:', index, updatedTeams[index]); // Debug
            } else {
                console.error('No team found at index:', index); // Debug
            }
            return updatedTeams;
        });
    };

    const loadTeamMembersForExistingTeam = async (teamId) => {
        const teamIndex = existingTeamsData.findIndex(team => team.team_id === teamId);
        if (teamIndex !== -1 && !existingTeamsData[teamIndex].allAvailableMembers) {
            const allMembers = await fetchAllTeamMembers(teamId);
            setExistingTeamsData(prevTeams => {
                const updatedTeams = [...prevTeams];
                updatedTeams[teamIndex] = {
                    ...updatedTeams[teamIndex],
                    allAvailableMembers: allMembers
                };
                return updatedTeams;
            });
        }
    };

    const handleDepartmentChange = async (index, newValue) => {
        setAddedTeams(prevAddedTeams => {
            const updatedTeams = [...prevAddedTeams];
            updatedTeams[index] = {
                ...updatedTeams[index],
                selectedDepartment: newValue,
                availableTeams: [],
                selectedTeam: null,
                teamMembersOptions: [],
                selectedTeamMembers: []
            };
            return updatedTeams;
        });

        if (newValue) {
            await fetchTeamsForAddedTeam(index, newValue.id);
        }
    };

    const handleTeamChange = async (index, newValue) => {
        setAddedTeams(prevAddedTeams => {
            const updatedTeams = [...prevAddedTeams];
            updatedTeams[index] = {
                ...updatedTeams[index],
                selectedTeam: newValue,
                teamMembersOptions: [],
                selectedTeamMembers: []
            };
            return updatedTeams;
        });

        if (newValue) {
            await fetchTeamMembersForAddedTeam(index, newValue.id);
        }
    };

    const fetchTeamMembersForAddedTeam = async (index, teamId) => {
        try {
            const response = await axiosInstance(localAxiosGet(`/organization/getAllUsersByTeamId/${teamId}`, accessToken));
            const teamMembersArray = response.data?.message?.users?.map((user) => ({
                id: user.id,
                firstName: user.first_name,
                lastName: user.last_name
            })) || [];

            setAddedTeams(prevAddedTeams => {
                const updatedTeams = [...prevAddedTeams];
                if (updatedTeams[index]) {
                    updatedTeams[index] = {
                        ...updatedTeams[index],
                        teamMembersOptions: teamMembersArray
                    };
                }
                return updatedTeams;
            });
        } catch (error) {
            console.error('Error in fetching team members data:', error);
        }
    };

    const handleTeamMembersChange = (index, newValue) => {
        setAddedTeams(prevAddedTeams => {
            const updatedTeams = [...prevAddedTeams];
            updatedTeams[index] = {
                ...updatedTeams[index],
                selectedTeamMembers: newValue
            };
            return updatedTeams;
        });
    };

    const handleRemoveAddedTeam = (index) => {
        setAddedTeams(prevAddedTeams =>
            prevAddedTeams.filter((_, i) => i !== index)
        );
    };

    const handleExistingTeamMembersChange = (teamId, newValue) => {
        setEditedExistingTeams(prev => ({ ...prev, [teamId]: newValue }));
    };

    const handleRemoveExistingTeam = (teamId) => {
        const updatedExistingTeams = existingTeamsData.filter(team => team.team_id !== teamId);
        setExistingTeamsData(updatedExistingTeams);
        setEditedExistingTeams(prev => {
            const newState = { ...prev };
            delete newState[teamId];
            return newState;
        });
    };

    const handleAssignTeams = async () => {
        if(!hasChanges()){
            setSnackbar({open:true,message:'No changes to save',severity:'info'});  
            return;
        }   

        if (!selectedProjectId) return;

        const newTeamMembersPayload = addedTeams.reduce((acc, teamData) => {
            if (teamData.selectedDepartment && teamData.selectedTeam && teamData.selectedTeamMembers) {
                const userIds = teamData.selectedTeamMembers.map(member => ({
                    department_id: teamData.selectedDepartment.id,
                    team_id: teamData.selectedTeam.id,
                    user_id: member.id
                }));
                return [...acc, ...userIds];
            }
            return acc;
        }, []);

        const existingTeamMembersPayload = existingTeamsData.reduce((acc, teamInfo) => {
            const teamMembers = editedExistingTeams[teamInfo.team_id] || [];
            const userIds = teamMembers.map(member => ({
                department_id: teamInfo.department_id,
                team_id: teamInfo.team_id,
                user_id: member.id
            }));
            return [...acc, ...userIds];
        }, []);

        const allTeamMembersPayload = [...newTeamMembersPayload, ...existingTeamMembersPayload];

        // Merge duplicate teams and remove duplicate users
        const mergedTeamMembers = new Map();

        allTeamMembersPayload.forEach(member => {
            const key = `${member.department_id}-${member.team_id}`;
            if (!mergedTeamMembers.has(key)) {
                mergedTeamMembers.set(key, new Set());
            }
            mergedTeamMembers.get(key).add(member.user_id);
        });

        // Convert back to the required format
        const finalPayload = [];
        mergedTeamMembers.forEach((userIds, key) => {
            const [department_id, team_id] = key.split('-').map(Number);
            userIds.forEach(user_id => {
                finalPayload.push({
                    department_id,
                    team_id,
                    user_id
                });
            });
        });

        const payloadForAssignmentApi = {
            project_id: selectedProjectId,
            team_members: finalPayload,
        };

        console.log('Final merged payload:', payloadForAssignmentApi);

        setIsLoading(true);

        try {
            const response = await axiosInstance(localAxiosToken('/project/updateAssignTeam', payloadForAssignmentApi));
            if (response.data.status == 'SUCCESS') {
                // Refresh the project details to show updated teams
                await fetchProjectDetails();

                // Clear only the added teams (new teams)
                setAddedTeams([]);

                // Show success message
                if (onTeamAssignSuccess) {
                    onTeamAssignSuccess();
                }

                console.log('Teams assigned successfully, dialog remains open');
            }
        } catch (error) {
            console.error("Error assigning teams:", error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!selectedProject || !projectDetails) {
        return null;
    }
    const getAvailableTeamMembers = (teamMembersOptions, currentSelectedMembers, currentTeamId = null, currentIndex = null) => {
        // Get all selected member IDs from ALL teams (including current one)
        const allSelectedIds = new Set();

        // From added teams
        addedTeams.forEach((team) => {
            if (team.selectedTeamMembers) {
                team.selectedTeamMembers.forEach(member => {
                    allSelectedIds.add(member.id);
                });
            }
        });

        // From existing teams
        Object.values(editedExistingTeams).forEach((teamMembers) => {
            if (teamMembers) {
                teamMembers.forEach(member => {
                    allSelectedIds.add(member.id);
                });
            }
        });

        // Return only members that are NOT selected anywhere
        return teamMembersOptions.filter(member => {
            return !allSelectedIds.has(member.id);
        });
    };
    const handleClose = () => {
        setAddedTeams([]);
        setEditedExistingTeams({});
        setExistingTeamsData([]);
        setProjectDetails(null);
        setInitialTeamsState({
            addedTeams: [],
            existingTeams: {}
        });
        onClose();
    }


    return (
        <StyledDialog open={open} maxWidth="1200px" fullWidth>
            <DialogTitle>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f2937' }}>
                    Project Team Assignment
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ padding: '24px', }}>
                {selectedProject && (
                    <Grid container spacing={3}>
                        {/* Project Details Section */}
                        <Grid item xs={12} lg={4}>
                            <StyledPaper>
                                <Typography variant="h6" sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: '16px'
                                }}>
                                    <AssignmentIcon />
                                    Project Details
                                </Typography>

                                <TableContainer component={Box} sx={{ mb: 3 }}>
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AssignmentIcon sx={{ color: '#3b82f6' }} />
                                                        Project Name
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={selectedProject.project_title || 'Not Assigned'}
                                                        sx={{
                                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                            color: '#3b82f6'
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <SupervisorAccountIcon sx={{ color: '#3b82f6' }} />
                                                        Manager
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={selectedProject.manager ? `${selectedProject.manager.first_name} ${selectedProject.manager.last_name}` : 'Not Assigned'}
                                                        sx={{
                                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                            color: '#3b82f6'
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccountCircleIcon sx={{ color: '#6366f1' }} />
                                                        Team Lead
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={selectedProject.team_leader ? `${selectedProject.team_leader.first_name} ${selectedProject.team_leader.last_name}` : 'Not Assigned'}
                                                        sx={{
                                                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                            color: '#6366f1'
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <FlagIcon sx={{ color: '#eab308' }} />
                                                        Priority
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={selectedProject.priority || 'Not Set'}
                                                        sx={{
                                                            backgroundColor: 'rgba(234, 179, 8, 0.1)',
                                                            color: '#eab308'
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>


                                            {/* Team Members Section */}                                         
                                            {projectDetails?.departments && projectDetails.departments.length > 0 && (
                                                <>
                                                    {projectDetails.departments.map((department) => (
                                                        <React.Fragment key={department.department_id}>
                                                            {/* Department Header Row */}
                                                            <TableRow>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <BusinessIcon sx={{ color: '#10b981' }} />
                                                                        Department
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={department.department_name}
                                                                        sx={{
                                                                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                                            color: '#10b981'
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>

                                                            {/* Teams and Members Rows */}
                                                            {department.teams && department.teams.length > 0 ? (
                                                                department.teams.map((team) => (
                                                                    <TableRow key={team.team_id}>
                                                                        <TableCell>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                                                                                <BusinessIcon sx={{ color: '#6366f1' }} />
                                                                                {team.team_name}
                                                                            </Box>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {team.members && team.members.length > 0 ? (
                                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                                    {team.members.map((member) => (
                                                                                        <Chip
                                                                                            key={member.id}
                                                                                            label={`${member.first_name} ${member.last_name}`}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                                                                color: '#6366f1',
                                                                                                fontSize: '11px'
                                                                                            }}
                                                                                        />
                                                                                    ))}
                                                                                </Box>
                                                                            ) : (
                                                                                <Chip
                                                                                    label="No members assigned"
                                                                                    size="small"
                                                                                    sx={{
                                                                                        backgroundColor: 'rgba(156, 163, 175, 0.1)',
                                                                                        color: '#9ca3af'
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={2} sx={{ pl: 4, color: '#9ca3af', fontStyle: 'italic' }}>
                                                                        No teams in this department
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </StyledPaper>
                        </Grid>

                        {/* Team Assignment Section - Right Side */}
                        <Grid item xs={12} lg={8}>
                            <StyledPaper sx={{ padding: '16px' }}>
                                <Typography variant="h6" sx={{
                                    mb: 2,
                                    fontWeight: 600,
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    fontSize: '16px'
                                }}>
                                    <GroupsIcon />
                                    Team Assignment
                                </Typography>

                                {/* Existing Teams Section */}
                                {existingTeamsData.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" sx={{
                                            mb: 2,
                                            fontWeight: 600,
                                            color: '#374151',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <GroupsIcon sx={{ color: '#10b981' }} />
                                            Existing Teams
                                        </Typography>

                                        {existingTeamsData.map((teamInfo) => (
                                            <TeamCard key={teamInfo.team_id} sx={{ mb: 2 }}>
                                                <CardContent sx={{ padding: '16px !important' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                        <Box>
                                                            <Typography variant="h6" sx={{
                                                                fontWeight: 600,
                                                                color: '#1f2937',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                                mb: 1
                                                            }}>
                                                                <BusinessIcon sx={{ color: '#6366f1' }} />
                                                                {teamInfo.department_name} - {teamInfo.team_name}
                                                            </Typography>

                                                        </Box>
                                                        <IconButton
                                                            onClick={() => handleRemoveExistingTeam(teamInfo.team_id)}
                                                            sx={{
                                                                color: '#ef4444',
                                                                '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                                                            }}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Box>

                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            multiple
                                                            options={teamInfo.allAvailableMembers || []}
                                                            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                                            value={editedExistingTeams[teamInfo.team_id] || []}
                                                            onChange={(event, newValue) => handleExistingTeamMembersChange(teamInfo.team_id, newValue)}
                                                            onOpen={() => loadTeamMembersForExistingTeam(teamInfo.team_id)}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Team Members"
                                                                    // placeholder="Select team members"
                                                                />
                                                            )}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => (
                                                                    <Chip
                                                                        variant="outlined"
                                                                        label={`${option.firstName} ${option.lastName}`}
                                                                        {...getTagProps({ index })}
                                                                        sx={{
                                                                            backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                                            borderColor: 'rgba(16, 185, 129, 0.3)',
                                                                            color: '#059669'
                                                                        }}
                                                                    />
                                                                ))
                                                            }
                                                        />
                                                    </FormControl>
                                                </CardContent>
                                            </TeamCard>
                                        ))}
                                    </Box>
                                )}


                                {/* Added Teams */}
                                {addedTeams.map((teamData, index) => (
                                    <TeamCard key={teamData.id} sx={{ mb: 2 }}>
                                        <CardContent sx={{ padding: '16px !important' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                <Typography variant="h6" sx={{
                                                    fontWeight: 600,
                                                    color: '#1f2937',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}>
                                                    <GroupAddIcon sx={{ color: '#3b82f6' }} />
                                                    New Team
                                                </Typography>
                                                <IconButton
                                                    onClick={() => handleRemoveAddedTeam(index)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                                                    }}
                                                >
                                                    <CancelIcon />
                                                </IconButton>
                                            </Box>

                                            <Grid container spacing={2}>
                                                {/* Department Selection */}

                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            options={departments || []}
                                                            getOptionLabel={(option) => option.department}
                                                            value={teamData.selectedDepartment}
                                                            onChange={(event, newValue) => handleDepartmentChange(index, newValue)}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Select Department"
                                                                    placeholder="Choose department"
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </Grid>

                                                {/* Team Selection */}
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            options={teamData.availableTeams || []}
                                                            getOptionLabel={(option) => option.name}
                                                            value={teamData.selectedTeam}
                                                            onChange={(event, newValue) => handleTeamChange(index, newValue)}
                                                            disabled={!teamData.selectedDepartment}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Select Team"
                                                                    placeholder="Choose team"
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </Grid>

                                                {/* Team Members Selection */}
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth>
                                                        <Autocomplete
                                                            multiple
                                                            options={getAvailableTeamMembers(
                                                                teamData.teamMembersOptions,
                                                                teamData.selectedTeamMembers,
                                                                null,
                                                                index
                                                            )}
                                                            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                                                            value={teamData.selectedTeamMembers}
                                                            onChange={(event, newValue) => handleTeamMembersChange(index, newValue)}
                                                            disabled={!teamData.selectedTeam}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Team Members"
                                                                //  placeholder="Select team members"
                                                                />
                                                            )}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, tagIndex) => (
                                                                    <Chip
                                                                        variant="outlined"
                                                                        label={`${option.firstName} ${option.lastName}`}
                                                                        {...getTagProps({ index: tagIndex })}
                                                                        sx={{
                                                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                                                            borderColor: 'rgba(59, 130, 246, 0.3)',
                                                                            color: '#3b82f6'
                                                                        }}
                                                                    />
                                                                ))
                                                            }
                                                        />
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </TeamCard>
                                ))}



                                {departments && departments.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<PersonAddIcon />}
                                            onClick={handleAddTeamMember}
                                            sx={{
                                                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                                                    boxShadow: '0 6px 16px rgba(59, 130, 246, 0.4)'
                                                }
                                            }}
                                        >
                                            Add Team
                                        </Button>
                                    </Box>
                                )}

                                {/* New Teams Section - Empty State Message */}
                                {addedTeams.length === 0 && existingTeamsData.length === 0 && departments && departments.length === 0 && (
                                    <Box sx={{ py: 2, textAlign: 'center', width: '100%' }}>
                                        <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                                            No departments available to add teams
                                        </Typography>
                                    </Box>
                                )}

                            </StyledPaper>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions sx={{}}>
                {(addedTeams.length > 0 || existingTeamsData.length > 0) && (
                    <Box >
                        <Button
                            variant="contained"
                            onClick={handleAssignTeams}
                            disabled={isLoading || !hasChanges()}
                            size="medium"
                            sx={{
                                '&:disabled': {
                                    backgroundColor: '#9ca3af',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {isLoading ? 'Saving...' : (addedTeams.length > 0 || Object.keys(editedExistingTeams).length > 0) ? 'Save Teams' : 'Teams Saved'}
                        </Button>
                    </Box>
                )}
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="medium"
                    sx={{

                        '&:hover': {
                            borderColor: '#374151',
                            backgroundColor: '#f9fafb'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>

            <Snackbar
        open={snackbar.open}
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
        </StyledDialog>
    );
};

export default UpdateAssignTeam;